import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { logger } from "@amzn/afxtaco-utilities";
import { AppDispatch } from "../../../redux/store";
import { TacoWebSocket } from "./types";
import { createWebSocket } from "./WebSocket";
import { webSocketOnMessageEventHandler } from "./WebSocketMessageEventHandler";

const SOCKET_TIMEOUT = 119 * 60 * 1000; // 119 mins in ms - 2 hour socket timeout: https://docs.aws.amazon.com/apigateway/latest/developerguide/apigateway-execution-service-websocket-limits-table.html
const CHECK_WEBSOCKET_STATUS_INTERVAL = 30 * 1000; // 30 seconds in ms
// Backoff applied to health-check reconnects after a failed attempt. Without it
// a persistently failing connect (expired session, no network) is retried every
// CHECK_WEBSOCKET_STATUS_INTERVAL indefinitely. The base is deliberately longer
// than that interval so the first failure already skips a tick.
const RECONNECT_BACKOFF_BASE = 2 * CHECK_WEBSOCKET_STATUS_INTERVAL; // 60 seconds in ms
const RECONNECT_BACKOFF_MAX = 5 * 60 * 1000; // 5 minutes in ms

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketContext = createContext<TacoWebSocket | undefined>(
  undefined,
);

/**
 * Hook to access the WebSocket connection from any component.
 * Must be used within a WebSocketProvider.
 *
 * @returns The TacoWebSocket instance (or undefined if not yet connected)
 *
 * @example
 * ```tsx
 * const ws = useWebSocket();
 * ws?.sendMessage(JSON.stringify({ action: "ping", ... }));
 * ```
 */
export const useWebSocket = (): TacoWebSocket | undefined => {
  return useContext(WebSocketContext);
};

/**
 * WebSocketProvider manages WebSocket connections for the application with automatic reconnection
 * and connection health monitoring.
 *
 * This provider component establishes and maintains a WebSocket connection, handling:
 * - Initial connection setup with authentication
 * - Automatic reconnection every 119 minutes (before AWS API Gateway's 2-hour timeout)
 * - Connection health checks every 30 seconds
 * - Graceful connection transitions to prevent message loss
 * - Error handling and logging
 *
 * The WebSocket connection is made available to child components via React Context.
 *
 * @param props - Component props
 * @param props.children - Child components that will have access to the WebSocket context
 *
 * @returns A React element that provides WebSocket context to its children
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <WebSocketProvider>
 *       <YourComponents />
 *     </WebSocketProvider>
 *   );
 * }
 * ```
 *
 * @remarks
 * - Automatically reconnects before AWS API Gateway's 2-hour connection limit
 * - Monitors connection status every 30 seconds and reconnects if closed
 * - Maintains previous connection during reconnection to prevent message loss,
 *   and retires it only once the replacement is open
 * - Dispatches incoming messages to Redux store via webSocketOnMessageEventHandler
 * - Holds at most one connection open at a time: overlapping creation attempts
 *   collapse into one, and unmount closes every connection this provider opened
 * - Backs off exponentially when connection attempts fail
 */
const WebSocketProvider = ({
  children,
}: WebSocketProviderProps): React.ReactElement => {
  const [tacoWebSocket, setTacoWebSocket] = useState<TacoWebSocket | undefined>(
    undefined,
  );
  // Use a ref to track the current WebSocket for use in intervals without triggering re-renders
  const tacoWebSocketRef = useRef<TacoWebSocket | undefined>(undefined);

  const dispatch = useDispatch<AppDispatch>();

  /**
   * Creates an event handler to process websocket events
   * It's in a useCallback because the function is used in useEffects
   * and we don't want it to constantly trigger
   */
  const onMessageEventHandler = useCallback(
    (event: MessageEvent) => webSocketOnMessageEventHandler(dispatch, event),
    [dispatch],
  );

  useEffect(() => {
    // Every socket this effect run has opened and not yet closed. Teardown
    // closes all of them, so no connection can outlive the provider. Tracking
    // only the "current" socket was not enough: a socket retired mid-rotation,
    // or one whose creation resolved after teardown, was left open server-side
    // with nothing able to close it.
    const liveSockets = new Set<TacoWebSocket>();
    // Set by teardown. A creation that resolves afterwards closes its socket
    // rather than adopting it.
    let cancelled = false;
    // Collapses overlapping creation attempts into one. createWebSocket takes
    // ~150ms, so anything that requests connections faster than that used to
    // open a separate connection per request.
    let connecting = false;
    let consecutiveFailures = 0;
    let nextAttemptAllowedAt = 0;

    const closeSocket = (target: TacoWebSocket | undefined) => {
      if (!target) {
        return;
      }
      liveSockets.delete(target);
      target.socket.close();
    };

    const adoptSocket = (socket: TacoWebSocket) => {
      // Only sockets that still need closing belong here. Drop any that have
      // already closed, otherwise the set grows for the lifetime of the page:
      // the health check replaces a closed socket without ever retiring it.
      for (const tracked of liveSockets) {
        if (tracked.socket.readyState === WebSocket.CLOSED) {
          liveSockets.delete(tracked);
        }
      }
      liveSockets.add(socket);
      // Assign the ref synchronously. It used to be assigned by a separate
      // effect that ran after React committed the state update, which left a
      // window where a live socket was invisible to teardown and to the
      // interval callbacks.
      tacoWebSocketRef.current = socket;
      setTacoWebSocket(socket);
    };

    const connectWebSocket = async (): Promise<TacoWebSocket | undefined> => {
      if (connecting || cancelled) {
        return undefined;
      }
      connecting = true;
      try {
        const newSocket = await createWebSocket({
          onMessageEventHandler,
        });
        if (cancelled) {
          newSocket.socket.close();
          return undefined;
        }
        adoptSocket(newSocket);
        consecutiveFailures = 0;
        nextAttemptAllowedAt = 0;
        return newSocket;
      } catch (error) {
        consecutiveFailures += 1;
        nextAttemptAllowedAt =
          Date.now() +
          Math.min(
            RECONNECT_BACKOFF_MAX,
            RECONNECT_BACKOFF_BASE * 2 ** (consecutiveFailures - 1),
          );
        logger.error({
          loggerMsg: "Error creating websocket. Try refreshing the page",
          error,
        });
        return undefined;
      } finally {
        connecting = false;
      }
    };

    // Create a new socket and close the old socket every 119 minutes.
    // Reason because websocket connection has a 2 hour maximum limit.
    // https://docs.aws.amazon.com/apigateway/latest/developerguide/apigateway-execution-service-websocket-limits-table.html
    const reconnectIntervalId = setInterval(() => {
      logger.log({
        loggerMsg: "Attempting to reconnect socket",
      });
      const previous = tacoWebSocketRef.current;
      // If a health-check reconnect happens to be in flight, connectWebSocket
      // returns undefined and this rotation is skipped. That only occurs when
      // the current socket is already closed, in which case the health check is
      // doing this job anyway.
      void connectWebSocket().then((newSocket) => {
        // Retire the previous socket only once its replacement is actually
        // open. Closing it unconditionally left the app with no connection at
        // all whenever the replacement failed.
        if (newSocket && previous && previous !== newSocket) {
          closeSocket(previous);
        }
      });
    }, SOCKET_TIMEOUT);

    // Check if status of the Websocket is closed every CHECK_WEBSOCKET_STATUS_INTERVAL seconds.
    // Create a new socket if Websocket status is closed.
    const healthCheckIntervalId = setInterval(() => {
      const current = tacoWebSocketRef.current;
      if (!current?.socket || current.socket.readyState !== WebSocket.CLOSED) {
        return;
      }
      if (Date.now() < nextAttemptAllowedAt) {
        return;
      }
      logger.log({
        loggerMsg:
          "Websocket is detected to be closed, creating new websocket.",
      });
      void connectWebSocket();
    }, CHECK_WEBSOCKET_STATUS_INTERVAL);

    void connectWebSocket();

    return () => {
      cancelled = true;
      clearInterval(reconnectIntervalId);
      clearInterval(healthCheckIntervalId);
      // Close every socket this run opened, not just the current one.
      for (const socket of liveSockets) {
        socket.socket.close();
      }
      liveSockets.clear();
      tacoWebSocketRef.current = undefined;
    };
  }, [onMessageEventHandler]);

  return (
    <WebSocketContext.Provider value={tacoWebSocket}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;
